import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { REVIEW_EVENT_TYPE_KEYS } from '@repo/constants'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const reviewEventsSearchSchema = z.object({
  keyword: z.string().optional(),
  erp: z.array(z.string()).optional(),
  typeKey: z.array(z.enum(REVIEW_EVENT_TYPE_KEYS)).optional(),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
})

export type ReviewEventsSearch = z.infer<typeof reviewEventsSearchSchema>

type ReviewEventsQueries = Omit<ReviewEventsSearch, 'createdFrom' | 'createdTo'> & {
  createdFrom?: string
  createdTo?: string
  timezone: string
  page: number
  pageSize: number
}

async function getReviewEvents(queries: ReviewEventsQueries, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['review-events'].$get(
    // `stringifyValues` widens every key to optional; `timezone` is always present, so restore it.
    { query: { ...stringifyValues(queries), timezone: queries.timezone } },
    { init: { signal } },
  )

  return await res.json()
}

export type ReviewEventRow = Awaited<ReturnType<typeof getReviewEvents>>['results'][number]

/** One page of the event log. Read-only: events are recorded by the system and never edited. */
export function useReviewEvents(search: ReviewEventsSearch & { pageSize: number }) {
  const queries: ReviewEventsQueries = {
    keyword: search.keyword,
    erp: search.erp,
    typeKey: search.typeKey,
    createdFrom: search.createdFrom ? formatDateOnly(search.createdFrom) : undefined,
    createdTo: search.createdTo ? formatDateOnly(search.createdTo) : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: search.page ?? 1,
    pageSize: search.pageSize,
  }

  return useQuery({
    queryKey: ['reviewEvents', queries],
    queryFn: ({ signal }) => getReviewEvents(queries, signal),
  })
}
